import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Components
import Navigation from "@/components/Navigation";
import PromoBanner from "@/components/PromoBanner";
import HeroBanner from "@/components/HeroBanner";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Services from "@/components/Services";
import BookingSection from "@/components/BookingSection";
import ClarityCall from "@/components/ClarityCall";
import CareerWheel from "@/components/CareerWheel";
import Testimonials from "@/components/Testimonials";
import FeedbackForm from "@/components/FeedbackForm";
import Blog from "@/components/Blog";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import PaymentModal from "@/components/PaymentModal";
import Poster from "@/components/Poster";
import ClarityPoster from "@/components/ClarityPoster";
import CareerWheelPoster from "@/components/CareerWheelPoster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchTestimonials();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data.services);
    } catch (e) {
      console.error("Error fetching services:", e);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/feedback?approved_only=true`);
      setTestimonials(response.data);
    } catch (e) {
      console.error("Error fetching testimonials:", e);
    }
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success("Discovery call booked successfully! We'll contact you soon.");
      return response.data;
    } catch (e) {
      toast.error("Failed to book. Please try again.");
      throw e;
    }
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      const response = await axios.post(`${API}/feedback`, feedbackData);
      toast.success("Thank you for your feedback!");
      return response.data;
    } catch (e) {
      toast.error("Failed to submit feedback. Please try again.");
      throw e;
    }
  };

  const handleContactSubmit = async (contactData) => {
    try {
      const response = await axios.post(`${API}/contact`, contactData);
      toast.success("Message sent! We'll get back to you soon.");
      return response.data;
    } catch (e) {
      toast.error("Failed to send message. Please try again.");
      throw e;
    }
  };

  const handlePaymentSelect = (service, pkg) => {
    setSelectedService(service);
    setSelectedPackage(pkg);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSubmit = async (paymentData) => {
    try {
      const response = await axios.post(`${API}/payments/create`, paymentData);
      // Bank transfer - just record the intent, no redirect needed
      return response.data;
    } catch (e) {
      // Still show bank details even if API call fails
      console.error("Payment record failed:", e);
      return null;
    }
  };

  return (
    <div className="min-h-screen">
      <PromoBanner />
      <Navigation />
      <main>
        <HeroBanner />
        <Hero />
        <About />
        <Services 
          services={services} 
          onPaymentSelect={handlePaymentSelect} 
        />
        <BookingSection onSubmit={handleBookingSubmit} />
        <ClarityCall />
        <CareerWheel />
        <Testimonials testimonials={testimonials} />
        <Blog />
        <FeedbackForm onSubmit={handleFeedbackSubmit} />
        <Contact onSubmit={handleContactSubmit} />
      </main>
      <Footer />
      
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        service={selectedService}
        selectedPackage={selectedPackage}
        onSubmit={handlePaymentSubmit}
      />
      
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/poster" element={<Poster />} />
        <Route path="/poster/clarity" element={<ClarityPoster />} />
        <Route path="/poster/career-wheel" element={<CareerWheelPoster />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
    </BrowserRouter>
  );
}

function PaymentSuccess() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold mb-2">Payment Successful!</h1>
        <p className="text-muted-foreground mb-6">Thank you for your purchase. We'll be in touch soon.</p>
        <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
          Return Home
        </a>
      </div>
    </div>
  );
}

function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold mb-2">Payment Cancelled</h1>
        <p className="text-muted-foreground mb-6">Your payment was cancelled. No charges were made.</p>
        <a href="/" className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
          Return Home
        </a>
      </div>
    </div>
  );
}

export default App;
